import { Outlet, ScrollRestoration } from "react-router-dom";
import '../global.css';
import { Helmet } from "react-helmet-async";
import { useTheme } from "@/theme/ThemeProvider";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { LivePlayerContext } from "@/contexts/LivePlayerContext";

import { AppFooterLayout } from "./AppFooterLayout";
import { AppHeaderModelo2Layout } from "./AppHeaderModelo2Layout";
import { Toaster } from "@/components/ui/toaster";
import { titulosDeCapitalizacao } from "@/utils/copyTituloBilhete";
import { Button } from "@/components/ui/button";
import ReactPlayer from "react-player";

type PlayerPosition = { x: number; y: number };

const PLAYER_MARGIN_RIGHT_PX = 16; // right-4
const PLAYER_MARGIN_BOTTOM_PX = 80; // bottom-20
const PLAYER_MD_BREAKPOINT = 768;
const PLAYER_PANEL_WIDTH_MOBILE = 240;
const PLAYER_PANEL_WIDTH_DESKTOP = 320;
const PLAYER_VIDEO_HEIGHT_MOBILE = 135;
const PLAYER_VIDEO_HEIGHT_DESKTOP = 180;

function clampPlayerPosition(position: PlayerPosition, width: number, height: number): PlayerPosition {
    const maxX = Math.max(0, window.innerWidth - width);
    const maxY = Math.max(0, window.innerHeight - height);

    return {
        x: Math.min(Math.max(0, position.x), maxX),
        y: Math.min(Math.max(0, position.y), maxY),
    };
}

function getInitialPlayerPosition(width: number, height: number): PlayerPosition {
    return clampPlayerPosition(
        {
            x: window.innerWidth - width - PLAYER_MARGIN_RIGHT_PX,
            y: window.innerHeight - height - PLAYER_MARGIN_BOTTOM_PX,
        },
        width,
        height,
    );
}

function useIsMdUp() {
    const [isMdUp, setIsMdUp] = useState(() =>
        typeof window !== "undefined"
            ? window.matchMedia(`(min-width: ${PLAYER_MD_BREAKPOINT}px)`).matches
            : true,
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(min-width: ${PLAYER_MD_BREAKPOINT}px)`);
        const onChange = () => setIsMdUp(mediaQuery.matches);

        onChange();
        mediaQuery.addEventListener("change", onChange);

        return () => {
            mediaQuery.removeEventListener("change", onChange);
        };
    }, []);

    return isMdUp;
}

export function AppLayout() {
    const { theme } = useTheme();
    const isMdUp = useIsMdUp();
    const panelWidth = isMdUp ? PLAYER_PANEL_WIDTH_DESKTOP : PLAYER_PANEL_WIDTH_MOBILE;
    const videoHeight = isMdUp ? PLAYER_VIDEO_HEIGHT_DESKTOP : PLAYER_VIDEO_HEIGHT_MOBILE;
    const [helmetTitle, setHelmetTitle] = useState('');
    const [livePlayerUrl, setLivePlayerUrl] = useState("");
    const [exibirMiniPlayerSorteioAoVivo, setExibirMiniPlayerSorteioAoVivo] = useState(false);
    const [playerPosition, setPlayerPosition] = useState<PlayerPosition | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const panelRef = useRef<HTMLDivElement>(null);
    const isDraggingRef = useRef(false);
    const dragOffsetRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
        const sufixo = titulosDeCapitalizacao();
        switch (theme) {
            case 'RondonCap':
                setHelmetTitle(`RondônCap | ${sufixo}`)
                break;
            case 'MegaCap':
                setHelmetTitle(`MegaCap | ${sufixo}`)
                break;
            case 'RoraiCap':
                setHelmetTitle(`RoraiCap | ${sufixo}`)
                break;
            case 'AcreCap':
                setHelmetTitle(`AcreCap | ${sufixo}`)
                break;

            default:
                break;
        }
    }, [theme])

    useEffect(() => {
        const onOpenLivePlayer = (event: Event) => {
            const customEvent = event as CustomEvent<{ url?: string }>;
            const url = customEvent.detail?.url?.trim();

            if (!url) {
                return;
            }

            setLivePlayerUrl(url);
            setExibirMiniPlayerSorteioAoVivo(true);
            setPlayerPosition(null);
            setIsDragging(false);
            isDraggingRef.current = false;
        };

        window.addEventListener("live-player-open", onOpenLivePlayer);

        return () => {
            window.removeEventListener("live-player-open", onOpenLivePlayer);
        };
    }, []);

    useLayoutEffect(() => {
        if (!exibirMiniPlayerSorteioAoVivo || !livePlayerUrl) {
            return;
        }

        const panel = panelRef.current;
        if (!panel) {
            return;
        }

        setPlayerPosition((prev) => {
            if (prev !== null) {
                return prev;
            }

            return getInitialPlayerPosition(panel.offsetWidth, panel.offsetHeight);
        });
    }, [exibirMiniPlayerSorteioAoVivo, livePlayerUrl]);

    useEffect(() => {
        if (!exibirMiniPlayerSorteioAoVivo) {
            return;
        }

        function reclampPosition() {
            const panel = panelRef.current;
            if (!panel) {
                return;
            }

            const { offsetWidth: width, offsetHeight: height } = panel;

            setPlayerPosition((prev) => {
                if (prev === null) {
                    return getInitialPlayerPosition(width, height);
                }

                return clampPlayerPosition(prev, width, height);
            });
        }

        window.addEventListener("resize", reclampPosition);
        reclampPosition();

        return () => {
            window.removeEventListener("resize", reclampPosition);
        };
    }, [exibirMiniPlayerSorteioAoVivo, panelWidth, videoHeight]);

    function fecharMiniPlayerSorteioAoVivo() {
        setExibirMiniPlayerSorteioAoVivo(false);
        setLivePlayerUrl("");
        setPlayerPosition(null);
        setIsDragging(false);
        isDraggingRef.current = false;
    }

    function onTitlePointerDown(event: ReactPointerEvent<HTMLSpanElement>) {
        if (event.pointerType === "mouse" && event.button !== 0) {
            return;
        }

        const panel = panelRef.current;
        if (!panel) {
            return;
        }

        event.preventDefault();

        const rect = panel.getBoundingClientRect();
        const currentPosition = playerPosition ?? { x: rect.left, y: rect.top };

        setPlayerPosition(currentPosition);
        setIsDragging(true);
        isDraggingRef.current = true;
        dragOffsetRef.current = {
            x: event.clientX - currentPosition.x,
            y: event.clientY - currentPosition.y,
        };

        event.currentTarget.setPointerCapture(event.pointerId);
    }

    function onTitlePointerMove(event: ReactPointerEvent<HTMLSpanElement>) {
        if (!isDraggingRef.current) {
            return;
        }

        const panel = panelRef.current;
        if (!panel) {
            return;
        }

        setPlayerPosition(
            clampPlayerPosition(
                {
                    x: event.clientX - dragOffsetRef.current.x,
                    y: event.clientY - dragOffsetRef.current.y,
                },
                panel.offsetWidth,
                panel.offsetHeight,
            ),
        );
    }

    function onTitlePointerUp(event: ReactPointerEvent<HTMLSpanElement>) {
        if (!isDraggingRef.current) {
            return;
        }

        isDraggingRef.current = false;
        setIsDragging(false);

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    }

    const livePlayerContextValue = useMemo(
        () => ({
            isOpen: exibirMiniPlayerSorteioAoVivo && !!livePlayerUrl,
            url: livePlayerUrl,
        }),
        [exibirMiniPlayerSorteioAoVivo, livePlayerUrl],
    );

    const playerPanelStyle = {
        width: panelWidth,
        ...(playerPosition
            ? {
                left: playerPosition.x,
                top: playerPosition.y,
                right: "auto" as const,
                bottom: "auto" as const,
            }
            : {}),
    };

    return (
        <LivePlayerContext.Provider value={livePlayerContextValue}>
            <div >
                <Toaster />
                <Helmet title={helmetTitle} />
                <AppHeaderModelo2Layout />
                <div className="min-h-96 lg:px-36 2xl:px-96">
                    <ScrollRestoration getKey={location => location.pathname} />
                    <Outlet />
                </div>
                <AppFooterLayout />

                {
                    exibirMiniPlayerSorteioAoVivo && livePlayerUrl && (
                        <div
                            ref={panelRef}
                            className={`fixed z-50 max-w-[calc(100vw-2rem)] rounded-md bg-background p-2 shadow-xl border ${playerPosition ? "" : "bottom-20 right-4"}`}
                            style={playerPanelStyle}
                        >
                            <div className="mb-2 flex items-center justify-between gap-2">
                                <span
                                    className={`flex-1 select-none text-xs font-semibold text-primary touch-none md:text-sm ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
                                    onPointerDown={onTitlePointerDown}
                                    onPointerMove={onTitlePointerMove}
                                    onPointerUp={onTitlePointerUp}
                                    onPointerCancel={onTitlePointerUp}
                                >
                                    Sorteio ao vivo
                                </span>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="h-7 shrink-0 px-2"
                                    onClick={fecharMiniPlayerSorteioAoVivo}
                                >
                                    Fechar
                                </Button>
                            </div>
                            <div
                                className="relative w-full overflow-hidden rounded-md [&_youtube-video]:!min-h-0 [&_youtube-video]:!min-w-0"
                                style={{ height: videoHeight }}
                            >
                                <ReactPlayer
                                    src={livePlayerUrl}
                                    controls
                                    pip={true}
                                    playing={exibirMiniPlayerSorteioAoVivo}
                                    width="100%"
                                    height={videoHeight}
                                    style={{
                                        display: "block",
                                        minWidth: 0,
                                        minHeight: 0,
                                        width: "100%",
                                        height: videoHeight,
                                    }}
                                />
                                {isDragging && (
                                    <div className="absolute inset-0 z-10" aria-hidden />
                                )}
                            </div>
                        </div>
                    )
                }
            </div>
        </LivePlayerContext.Provider>
    )
}
